import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Input, ModalBody, ModalFooter, ModalHeader, Modal as RSModal } from 'reactstrap';

import { isCategoryValid, validServiceType } from '~support-at-home/lib/derive';

import { Button, ClickableIcon, ErrorResponse, Modal } from '~components';
import { DateInput } from '~components/form-fields/inputs';
import { faTrash } from '~components/icon';
import { Row, TableView, TableViewHandle } from '~components/table-view-v3';

import { PricePlanBillingRule, PricePlanModalRow, PricePlanService } from '~support-at-home/models';

import { columnsFactory } from '~support-at-home/forms/price-plans/pricePlanServicesList';

import { getAll as getAllServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getShifts } from '~lookups/actions/shifts';
import {
  create as createRevision,
  getOne as getCompanyPricePlanRevision,
} from '~support-at-home/actions/pricePlanRevisions';
import {
  create as createPlan,
  deleteOne as deletePlan,
  getOne as getCompanyPricePlan,
} from '~support-at-home/actions/pricePlans';

import { clear as clearRevision } from '~support-at-home/reducers/pricePlanRevisions';
import { clearPlan } from '~support-at-home/reducers/pricePlans';

import { selectAppointmentServiceTypes, selectLoading as selectServiceTypesLoading } from '~appointments/selectors/serviceTypes';
import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectPricePlanShifts } from '~lookups/selectors/shifts';
import {
  selectPricePlanRevision,
  selectPricePlanRevisionsLoading,
  selectErrorResponse as selectRevisionError,
} from '~support-at-home/selectors/pricePlanRevisions';
import { selectErrorResponse as selectPlanError, selectPricePlan } from '~support-at-home/selectors/pricePlans';
import { selectSahServiceTypeAssociations } from '~support-at-home/selectors/sahServiceTypeAssociations';

interface ComponentProps {
  planId: string | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const PricePlanModal: React.FC<ComponentProps> = ({ planId, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();

  const initialPlanData = { name: '', start_date: '', end_date: '', tableData: [] as PricePlanModalRow[] };
  const [initialTableData, setInitialTableData] = useState<PricePlanModalRow[]>([]);
  const [planData, setPlanData] = useState(initialPlanData);
  const [isProcessing, setIsProcessing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const plan = useAppSelector(selectPricePlan);
  const revision = useAppSelector(selectPricePlanRevision);
  const categories = useAppSelector(selectSahCategories);
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const serviceTypesLoading = useAppSelector(selectServiceTypesLoading);
  const serviceTypeAssocs = useAppSelector(selectSahServiceTypeAssociations);
  const shifts = useAppSelector(selectPricePlanShifts);

  const planError = useAppSelector(selectPlanError);
  const revisionLoading = useAppSelector(selectPricePlanRevisionsLoading);
  const revisionError = useAppSelector(selectRevisionError);

  const isNew = planId === null;
  const isLoading = revisionLoading === 'pending' && revision === undefined;

  useEffect(() => {
    if (isOpen) {
      dispatch(getShifts());
      dispatch(getAllServiceTypes({ deleted: false }));

      if (!isNew && planId) {
        dispatch(getCompanyPricePlan({ id: planId }));
      }
    } else {
      onClear();
    }
  }, [isOpen, planId]);

  useEffect(() => {
    if (plan && plan.revisions && plan.revisions.length > 0) {
      dispatch(getCompanyPricePlanRevision({ id: plan.revisions[0].id }));
    }
  }, [plan]);

  useEffect(() => {
    if (!plan && !revision) return;

    const mergedData = {
      name: plan?.name ?? '',
      start_date: plan?.start_date ?? '',
      end_date: plan?.end_date ?? '',
      tableData: [] as PricePlanModalRow[],
    };

    if (revision && (revision.services?.length > 0 || revision.billing_rules?.length > 0)) {
      const { services, billing_rules } = revision;
      const tableData = pivotData(services, billing_rules);
      mergedData.tableData = tableData;
      setInitialTableData(tableData.map((row) => ({ ...row })));
    }

    setPlanData(mergedData);
  }, [plan, revision]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!planData.name?.trim()) errors.name = 'Name is required';
    if (!planData.start_date) errors.start_date = 'Start date is required';
    if (!planData.end_date) errors.end_date = 'End date is required';
    else if (planData.start_date && new Date(planData.end_date) < new Date(planData.start_date)) {
      errors.end_date = 'End date must be after start date';
    }

    if (planData.tableData.length === 0) errors.tableData = 'At least one service must be added';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onPlanChange = (name: string, value: string) => {
    setPlanData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const pivotData = (services: PricePlanService[], billing_rules: PricePlanBillingRule[]): PricePlanModalRow[] => {
    const grouped: Record<string, PricePlanModalRow> = {};

    services.forEach((svc) => {
      const key = `${svc.service_type_id}`;
      if (!grouped[key]) {
        grouped[key] = {
          id: key,
          category_id: svc.category_id,
          service_type_id: svc.service_type_id,
        };
      }

      grouped[key][svc.shift_id] = svc.cost;
    });

    // merge billing rules - add new rows if needed
    billing_rules.forEach((rule) => {
      const key = `${rule.service_type_id}`;

      if (!grouped[key]) {
        grouped[key] = {
          id: key,
          category_id: rule.category_id,
          service_type_id: rule.service_type_id,
          billing_type: rule.billing_type,
        };
      } else {
        grouped[key].billing_type = rule.billing_type;
      }
    });

    return Object.values(grouped);
  };

  const unpivotData = (pivoted: PricePlanModalRow[]): any => {
    const services: any[] = [];
    const billing_rules: any[] = [];

    pivoted.forEach((row) => {
      const { service_type_id, category_id, billing_type, ...shiftCosts } = row;

      if (billing_type) {
        billing_rules.push({ billing_type, service_type_id, category_id });
      }

      const exclude = new Set(['id', 'service_type_id', 'service_type', 'billing_type']);
      Object.entries(shiftCosts).forEach(([shift_id, cost]) => {
        if (exclude.has(shift_id)) return;
        const num = typeof cost === 'number' ? cost : Number(cost);
        if (!Number.isFinite(num)) return;
        services.push({ category_id, service_type_id, shift_id, cost: num });
      });
    });

    return { services, billing_rules };
  };

  const onSave = async () => {
    setIsProcessing(true);
    const fieldsValid = validate();
    const servicesValid = (await tableRef.current?.validate()) ?? true;
    if (!fieldsValid || !servicesValid) {
      setIsProcessing(false);
      return;
    }

    const { services, billing_rules } = unpivotData([...planData.tableData] as PricePlanModalRow[]);

    const sanitisedServices = services.map(({ service_type_id, shift_id, cost, category_id }: any) => ({
      service_type_id,
      shift_id,
      cost,
      category_id,
    }));

    let payload;
    const { tableData, ...rest } = planData;
    if (isNew) {
      ({ payload } = await dispatch(createPlan({ ...rest, revision: { services: sanitisedServices, billing_rules } })));
    } else {
      ({ payload } = await dispatch(createRevision({ plan_id: planId, services: sanitisedServices, billing_rules })));
    }
    if (payload?.success) onClose(true);

    setIsProcessing(false);
  };

  const onDelete = async () => {
    if (!planId) {
      setDeleteModalOpen(false);
      return;
    }

    setIsProcessing(true);
    try {
      const { payload } = await dispatch(deletePlan({ id: planId }));
      if (payload?.success) onClose(true);
    } finally {
      setIsProcessing(false);
      setDeleteModalOpen(false);
    }
  };

  const onClear = () => {
    dispatch(clearPlan({}));
    dispatch(clearRevision([]));
    setPlanData(initialPlanData);
    setValidationErrors({});
    setInitialTableData([]);
  };

  // table setup
  const tableRef = useRef<TableViewHandle>(null);
  const pricePlanServiceColumns = useMemo(() => {
    return columnsFactory(categories, serviceTypes, serviceTypeAssocs, shifts);
  }, [categories, serviceTypes, serviceTypeAssocs, shifts]);

  const handleRowChange = async (allRows: Row[]) => {
    setPlanData((prev) => ({ ...prev, tableData: allRows as PricePlanModalRow[] }));
  };

  const isNewRow = (row: Row) => {
    return !initialTableData.find(({ id }) => id === row.id);
  };

  const isRowInvalid = (row: Row) => {
    // Guard against the loading window: serviceTypes arrives async and may be empty while pending.
    // Checking actual load state avoids a false positive when a company genuinely has zero service types.
    if (serviceTypesLoading === 'pending') return false;
    return (
      row.category_id === null ||
      !validServiceType(row, serviceTypes) ||
      !isCategoryValid(row, serviceTypeAssocs, categories)
    );
  };

  const canDeleteRow = (row: Row) => {
    // New rows can always be deleted.
    // Existing rows with an invalid/missing service type association (e.g. a grandfathered excluded service type)
    // can also be deleted so users can clean them up without being blocked.
    return isNewRow(row) || isRowInvalid(row);
  };

  const isRowDisabled = (row: Row) => {
    const isExisting = !isNewRow(row);
    return isExisting && isRowInvalid(row);
  };

  const canRowValidate = (row: Row) => {
    const isExisting = !isNewRow(row);
    return !(isExisting && isRowInvalid(row));
  };

  const header = `Price Plan - ${isNew ? 'New' : 'Update'}`;
  const error = [planError, revisionError].filter(Boolean).join(' ');

  return (
    <RSModal isOpen={isOpen} size="xl" style={{ maxWidth: '1800px', width: '100%' }} data-cy="price-plan-modal">
      <ModalHeader className="bg-success text-white" data-cy="price-plan-modal-header">
        {header}
      </ModalHeader>
      <ModalBody>
        <Modal
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          headerClassName="bg-warning"
          header="Warning"
          includeFooter={true}
          footer={
            <div>
              <Button size="sm" className="ps-4 pe-4 mx-2" color="danger" loading={isProcessing} onClick={onDelete}>
                Delete
              </Button>
              <Button
                size="sm"
                className="ps-4 pe-4 mx-2"
                data-cy="price-plan-delete-cancel-button"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </Button>
            </div>
          }
        >
          <div className="mb-4">Are you sure you want to delete price plan?</div>
          <div>This action will fail if price plan is attached to any ACTIVE budgets.</div>
          <div className="fw-bold mt-4">This is an irreversible action</div>
        </Modal>
        <div className="row d-flex justify-content-between my-2">
          <div className="col me-2">
            <label>Name</label>
            <Input
              id="name"
              name="name"
              data-cy="price-plan-name-input"
              value={planData?.name ?? ''}
              disabled={!isNew}
              onChange={({ target: { name, value } }) => onPlanChange(name, value)}
              invalid={!!validationErrors.name}
            />
            {validationErrors.name && (
              <div className="text-danger small" data-cy="name-validation-error">
                {validationErrors.name}
              </div>
            )}
          </div>

          <div className="col col-sm-3 me-2">
            <label>Start Date</label>
            <div className={validationErrors.start_date ? 'border border-danger rounded' : ''}>
              <DateInput
                type="date"
                id="start_date"
                name="start_date"
                dataCy="price-plan"
                value={planData?.start_date ?? ''}
                disabled={!isNew}
                onChange={onPlanChange}
              />
            </div>
            {validationErrors.start_date && (
              <div className="text-danger small" data-cy="start-date-validation-error">
                {validationErrors.start_date}
              </div>
            )}
          </div>

          <div className="col col-sm-3">
            <label>End Date</label>
            <div className={validationErrors.end_date ? 'border border-danger rounded' : ''}>
              <DateInput
                type="date"
                id="end_date"
                name="end_date"
                dataCy="price-plan"
                value={planData?.end_date ?? ''}
                disabled={!isNew}
                onChange={onPlanChange}
              />
            </div>
            {validationErrors.end_date && (
              <div className="text-danger small" data-cy="end-date-validation-error">
                {validationErrors.end_date}
              </div>
            )}
          </div>
        </div>
        <ErrorResponse message={error} data-cy="price-plan-error-response" />
        <TableView
          ref={tableRef}
          id="price-plan-services-table"
          data={planData.tableData}
          isLoading={isLoading}
          columns={pricePlanServiceColumns}
          onChange={handleRowChange}
          canDeleteRow={canDeleteRow}
          isRowDisabled={isRowDisabled}
          canRowValidate={canRowValidate}
          context={{ allRows: planData.tableData, initialRows: initialTableData }}
          editonly
          compactView
          bordered
        />
        {validationErrors.tableData && (
          <div className="text-danger small" data-cy="table-data-validation-error">
            {validationErrors.tableData}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <div>
          {!isNew && (
            <ClickableIcon
              id="price-plan-delete-icon"
              icon={faTrash}
              className="mx-2"
              color="danger"
              onClick={() => setDeleteModalOpen(true)}
              toolTip="Delete plan"
            />
          )}
        </div>
        <div>
          <Button
            size="sm"
            color="success"
            className="ps-4 pe-4 mx-2"
            data-cy="price-plan-confirm-button"
            loading={isProcessing}
            onClick={onSave}
          >
            Confirm
          </Button>
          <Button
            size="sm"
            className="ps-4 pe-4 mx-2"
            data-cy="price-plan-cancel-button"
            onClick={() => onClose(false)}
          >
            Cancel
          </Button>
        </div>
      </ModalFooter>
    </RSModal>
  );
};

export default PricePlanModal;
